/**
 * Customer booking limits (Oct 2026), ported from Openings
 * `packages/utils/src/bookingWindow.ts` so the website cuts its date picker
 * and words its notes exactly as the Openings booking page does. The API is
 * the authority: it leaves out the days past the window and the starts
 * inside a service's notice, and refuses a create for either. Everything
 * here is presentation — the picker stops where the API would answer an
 * empty day, and the notes say why.
 *
 * Two limits:
 *
 * - **Booking window** — `Salon.customerBookingHorizonDays`: how far ahead
 *   customers may book. Date-based in the location's wall-clock zone: today
 *   is day 0 and today + N is the last bookable date.
 * - **Minimum notice** — `Service.minBookingNoticeMinutes`: how soon before
 *   a start. A member clone takes its host service's value, and with several
 *   services in one booking the largest applies.
 */

const DB_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Matches Openings' `Salon_customerBookingHorizonDays_range` constraint. */
export const MAX_CUSTOMER_BOOKING_HORIZON_DAYS = 730;

/** A stored horizon as a whole number of days, or null for "no limit". */
export function normalizeCustomerBookingHorizonDays(
  value: unknown,
): number | null {
  if (typeof value !== "number" || !Number.isInteger(value)) return null;
  if (value < 1 || value > MAX_CUSTOMER_BOOKING_HORIZON_DAYS) return null;
  return value;
}

/**
 * The calendar date (`YYYY-MM-DD`) of `instant` in `timeZone`. A missing or
 * unknown zone falls back to the runtime's own zone.
 */
export function calendarDateInTimeZone(
  instant: Date,
  timeZone?: string | null,
): string {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(instant);
      const part = (type: string) =>
        parts.find((p) => p.type === type)?.value ?? "";
      return `${part("year")}-${part("month")}-${part("day")}`;
    } catch {
      // Unknown zone: fall through to the runtime zone.
    }
  }
  const month = String(instant.getMonth() + 1).padStart(2, "0");
  const day = String(instant.getDate()).padStart(2, "0");
  return `${instant.getFullYear()}-${month}-${day}`;
}

/** `date` plus `days` calendar days, both `YYYY-MM-DD`. */
export function addDaysToDbDate(date: string, days: number): string {
  const match = DB_DATE_PATTERN.exec(date);
  if (!match) throw new Error(`Invalid date: ${date}`);
  const shifted = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days),
  );
  return shifted.toISOString().slice(0, 10);
}

export interface CustomerBookingWindowInput {
  horizonDays: number | null | undefined;
  timeZone?: string | null;
  now?: Date;
}

/**
 * The last date customers may book (`YYYY-MM-DD`), or null when the business
 * set no limit.
 */
export function customerBookingWindowEndDate({
  horizonDays,
  timeZone,
  now = new Date(),
}: CustomerBookingWindowInput): string | null {
  const days = normalizeCustomerBookingHorizonDays(horizonDays);
  if (days === null) return null;
  return addDaysToDbDate(calendarDateInTimeZone(now, timeZone), days);
}

/** True when customers may not book `date` (`YYYY-MM-DD`) yet. */
export function isBeyondCustomerBookingWindow(
  date: string,
  input: CustomerBookingWindowInput,
): boolean {
  const endDate = customerBookingWindowEndDate(input);
  // `YYYY-MM-DD` strings order the same way as the dates they name.
  return endDate !== null && date > endDate;
}

const HORIZON_LABELS: Record<number, string> = {
  7: "1 week",
  14: "2 weeks",
  30: "1 month",
  60: "2 months",
  90: "3 months",
  180: "6 months",
  365: "1 year",
};

/** "3 months" for 90, "45 days" for a value without a named preset. */
export function describeCustomerBookingHorizon(days: number): string {
  return HORIZON_LABELS[days] ?? `${days} ${days === 1 ? "day" : "days"}`;
}

/**
 * Openings `booking.date.horizonNote`, byte-identical: shown under the
 * booking date picker when the business limits how far ahead customers can
 * book.
 */
export function getCustomerBookingHorizonNote(
  horizonDays: number | null | undefined,
): string | null {
  const days = normalizeCustomerBookingHorizonDays(horizonDays);
  if (days === null) return null;
  return `Bookings open up to ${describeCustomerBookingHorizon(days)} ahead.`;
}

// ---------------------------------------------------------------------------
// Minimum notice (per service)
// ---------------------------------------------------------------------------

/** Matches Openings' `Service_minBookingNoticeMinutes_range` constraint. */
export const MAX_MIN_BOOKING_NOTICE_MINUTES = 43200;

/** A stored notice as whole minutes, or null for "no notice". */
export function normalizeMinBookingNoticeMinutes(
  value: unknown,
): number | null {
  if (typeof value !== "number" || !Number.isInteger(value)) return null;
  if (value < 1 || value > MAX_MIN_BOOKING_NOTICE_MINUTES) return null;
  return value;
}

/**
 * The notice that binds a service: a member clone's host service value wins,
 * the same rule as `hasConsultation`. Pass `undefined` for a host service
 * (no reference) and the host row, or null when it was not found, for a
 * clone.
 */
export function resolveEffectiveMinBookingNotice(
  service: { minBookingNoticeMinutes?: number | null },
  referenceService?: { minBookingNoticeMinutes?: number | null } | null,
): number | null {
  return normalizeMinBookingNoticeMinutes(
    referenceService
      ? referenceService.minBookingNoticeMinutes
      : service.minBookingNoticeMinutes,
  );
}

/** The largest notice among the booked services, or null when none has one. */
export function maxMinBookingNotice(
  values: ReadonlyArray<number | null | undefined>,
): number | null {
  let max: number | null = null;
  for (const value of values) {
    const minutes = normalizeMinBookingNoticeMinutes(value);
    if (minutes !== null && (max === null || minutes > max)) max = minutes;
  }
  return max;
}

const NOTICE_LABELS: Record<number, string> = {
  60: "1 hour",
  120: "2 hours",
  240: "4 hours",
  720: "12 hours",
  1440: "24 hours",
  2880: "48 hours",
  10080: "1 week",
};

/** "24 hours" for 1440; whole hours or minutes for a value without a preset. */
export function describeMinBookingNotice(minutes: number): string {
  const named = NOTICE_LABELS[minutes];
  if (named) return named;
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
}

/**
 * Openings `booking.service.minNoticeNote`, byte-identical: shown with a
 * selected service that requires a minimum notice, before a time is chosen.
 */
export function getMinBookingNoticeNote(
  noticeMinutes: number | null | undefined,
): string | null {
  const minutes = normalizeMinBookingNoticeMinutes(noticeMinutes);
  if (minutes === null) return null;
  return `Book at least ${describeMinBookingNotice(minutes)} ahead.`;
}
